import { useEffect, useState } from 'react'
import PageHeader from '../components/PageHeader.jsx'
import Pagination from '../components/Pagination.jsx'
import { useToast } from '../components/ToastProvider.jsx'
import * as projectApi from '../services/projectApi'

const PAGE_SIZES = [20, 50, 100]

const EMPTY_CREATE = {
  name: '',
  description: '',
  sourceFolderName: '',
}

function activeLabel(project) {
  return project?.active === false ? '비활성' : '활성'
}

function parseLimit(value) {
  const n = Number.parseInt(value, 10)
  return PAGE_SIZES.includes(n) ? n : 20
}

function ProjectModal({ open, title, project, onClose, onSubmit }) {
  const [form, setForm] = useState(EMPTY_CREATE)
  const [active, setActive] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const isEdit = Boolean(project)

  useEffect(() => {
    if (!open) return
    setForm({
      name: project?.name || '',
      description: project?.description || '',
      sourceFolderName: project?.sourceFolderName || '',
    })
    setActive(project ? project.active !== false : true)
    setError('')
  }, [open, project])

  if (!open) return null

  async function handleSubmit(event) {
    event.preventDefault()
    if (!form.name.trim()) {
      setError('프로젝트명을 입력해 주세요.')
      return
    }
    setSaving(true)
    setError('')
    try {
      await onSubmit({
        name: form.name.trim(),
        description: form.description.trim(),
        sourceFolderName: isEdit ? form.sourceFolderName : form.sourceFolderName.trim(),
        active,
      })
    } catch (err) {
      setError(err.message || '권한이 없습니다.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <form className="modal-panel modal-panel--form" onClick={(event) => event.stopPropagation()} onSubmit={handleSubmit}>
        <h2 className="modal-title">{title}</h2>
        <label className="field">
          <span>프로젝트명</span>
          <input
            className="input"
            value={form.name}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
            placeholder="예: KERI 30KW DC DC Converter"
          />
        </label>
        <label className="field">
          <span>설명</span>
          <textarea
            className="input input--area"
            rows={3}
            value={form.description}
            onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
            placeholder="선택 입력"
          />
        </label>
        {isEdit ? (
          <label className="field">
            <span>기존 폴더명</span>
            <input className="input" value={form.sourceFolderName || '-'} readOnly />
          </label>
        ) : (
          <label className="field">
            <span>기존 폴더명 (선택)</span>
            <input
              className="input"
              value={form.sourceFolderName}
              onChange={(event) => setForm((current) => ({ ...current, sourceFolderName: event.target.value }))}
              placeholder="회사 폴더명이 있으면 그대로 보관"
            />
          </label>
        )}
        <label className="check">
          <input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} />
          사용 중
        </label>
        {error ? <p className="form-error">{error}</p> : null}
        <div className="modal-actions">
          <button type="button" className="btn btn--ghost btn--touch" onClick={onClose}>
            취소
          </button>
          <button type="submit" className="btn btn--primary btn--touch" disabled={saving}>
            {saving ? '저장 중...' : isEdit ? '저장' : '등록'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default function ProjectsPage() {
  const { show } = useToast()
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)

  async function load(nextPage = page, nextLimit = limit) {
    setLoading(true)
    setError('')
    try {
      const result = await projectApi.fetchProjectsPage({
        page: nextPage,
        limit: nextLimit,
      })
      setProjects(result.items)
      setTotal(result.total)
      setTotalPages(result.totalPages)
      setPage(result.page)
      setLimit(result.limit)
    } catch (err) {
      setError(err.message || '프로젝트 목록을 불러오지 못했습니다.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load(page, limit)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reloadKey로 생성/수정 후 재조회
  }, [page, limit, reloadKey])

  function changePage(nextPage) {
    setPage(nextPage)
  }

  function changeLimit(nextLimit) {
    const safe = parseLimit(nextLimit)
    setLimit(safe)
    setPage(1)
  }

  async function handleCreated(payload) {
    await projectApi.createProject(payload)
    show('프로젝트를 등록했습니다.')
    setCreateOpen(false)
    setPage(1)
    setReloadKey((current) => current + 1)
  }

  async function handleUpdated(payload) {
    await projectApi.updateProject(editing.id, payload)
    show('프로젝트 정보를 수정했습니다.')
    setEditing(null)
    setReloadKey((current) => current + 1)
  }

  async function toggleActive(item) {
    try {
      await projectApi.updateProject(item.id, { active: item.active === false })
      show(item.active === false ? '프로젝트를 활성화했습니다.' : '프로젝트를 비활성화했습니다.')
      setReloadKey((current) => current + 1)
    } catch (err) {
      setError(err.message || '상태를 변경하지 못했습니다.')
    }
  }

  return (
    <div className="page">
      <PageHeader
        title="프로젝트 관리"
        description="품목에서 선택할 사용 프로젝트를 등록하고 수정합니다. 삭제는 하지 않고 비활성화합니다."
        actions={
          <button type="button" className="btn btn--primary btn--touch" onClick={() => setCreateOpen(true)}>
            새 프로젝트
          </button>
        }
      />

      {error ? <p className="form-error">{error}</p> : null}

      <p className="result-count">
        {loading ? '불러오는 중...' : `총 ${total.toLocaleString('ko-KR')}건`}
      </p>

      {projects.length === 0 && !loading ? (
        <p className="empty">등록된 프로젝트가 없습니다.</p>
      ) : (
        <>
          <div className="card-list">
            {projects.map((item) => (
              <article key={item.id} className="part-card">
                <div className="part-card__top">
                  <span className="muted">{item.sourceFolderName || '직접 등록'}</span>
                  <span className="badge-row">
                    <span className={`badge ${item.active === false ? 'badge--pending' : 'badge--ok'}`}>
                      {activeLabel(item)}
                    </span>
                  </span>
                </div>
                <strong>{item.name}</strong>
                <span>{item.description || '설명 없음'}</span>
                <div className="part-card__actions">
                  <button type="button" className="btn btn--ghost btn--touch" onClick={() => setEditing(item)}>
                    수정
                  </button>
                  <button type="button" className="btn btn--ghost btn--touch" onClick={() => toggleActive(item)}>
                    {item.active === false ? '활성화' : '비활성화'}
                  </button>
                </div>
              </article>
            ))}
          </div>

          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>프로젝트명</th>
                  <th>기존 폴더명</th>
                  <th>설명</th>
                  <th>상태</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {projects.map((item) => (
                  <tr key={item.id}>
                    <td>{item.name}</td>
                    <td>{item.sourceFolderName || '-'}</td>
                    <td>{item.description || '-'}</td>
                    <td>{activeLabel(item)}</td>
                    <td>
                      <div className="user-actions">
                        <button type="button" className="btn btn--ghost btn--small" onClick={() => setEditing(item)}>
                          수정
                        </button>
                        <button type="button" className="btn btn--ghost btn--small" onClick={() => toggleActive(item)}>
                          {item.active === false ? '활성화' : '비활성화'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        limit={limit}
        disabled={loading}
        itemLabel="건"
        limitLabel="페이지당 프로젝트 수"
        onPageChange={changePage}
        onLimitChange={changeLimit}
      />

      <ProjectModal open={createOpen} title="새 프로젝트" onClose={() => setCreateOpen(false)} onSubmit={handleCreated} />
      <ProjectModal
        open={Boolean(editing)}
        title="프로젝트 수정"
        project={editing}
        onClose={() => setEditing(null)}
        onSubmit={handleUpdated}
      />
    </div>
  )
}
